import React, { useState } from 'react';
import { Heart, Linkedin, Twitter, Instagram, ArrowRight } from 'lucide-react';
import { socialLinks } from '../data/socialLinks';

const footerLinks = {
  Platform: [
    { label: 'About', href: '#about' },
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Assessments', href: '#reports' },
    { label: 'Reports', href: '#reports' },
    { label: 'Parent Dashboard', action: 'parent-dashboard' },
  ],
  Services: [
    { label: 'Individual Assessment', href: '#plans' },
    { label: 'Standard Diagnostic', href: '#plans' },
    { label: '3-Month Plan', href: '#plans' },
    
    { label: 'School Programs', href: '#plans' },
  ],
  Community: [
    { label: 'Our Team', href: '/team' },
    { label: 'Join Our Organisation', action: 'join' },
    {label: 'Career Opportunity', action: 'careers' },
    
  ],
  Legal: [
    { label: 'Privacy Policy', href: '#contact' },
    { label: 'Terms & Conditions', href: '#contact' },
    { label: 'Data Protection', href: '#contact' },
    { label: 'Disclaimer', href: '#contact' },
  ],
};

const Footer = ({ onOpenJoin, onOpenCareerLibrary, onOpenParentDashboard, onNavigateSection }) => {
  const [email, setEmail] = useState('');
  const [newsletterMessage, setNewsletterMessage] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState('idle');

  const handleNav = (href) => {
    if (!href || href === '#') return;
    if (onNavigateSection) {
      onNavigateSection(href);
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const handleFooterLink = (link) => {
    if (link.action === 'join') return onOpenJoin?.();
    if (link.action === 'careers') return onOpenCareerLibrary?.();
    if (link.action === 'parent-dashboard') return onOpenParentDashboard?.();
    return handleNav(link.href);
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    if (!isValidEmail) {
      setNewsletterStatus('error');
      setNewsletterMessage('Enter a valid email to get MindVrik updates.');
      return;
    }
    setNewsletterStatus('success');
    setNewsletterMessage('Thanks. MindVrik updates will reach you soon.');
    setEmail('');
  };

  return (
    <footer
      style={{
        background: '#1A202C',
        color: 'white',
        padding: '72px 24px 32px',
        position: 'relative',
        overflow: 'hidden',
      }}
      data-testid="footer"
      id="footer"
    >
      {/* Background ambient */}
      <div style={{
        position: 'absolute', top: '-100px', left: '50%', transform: 'translateX(-50%)',
        width: '600px', height: '400px',
        background: 'radial-gradient(ellipse, rgba(42,157,143,0.12) 0%, transparent 70%)',
        borderRadius: '50%', pointerEvents: 'none',
      }} />

      <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr', gap: '48px', marginBottom: '64px' }}>

          {/* Brand column */}
          <div>
            <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.55)', lineHeight: 1.75, marginBottom: '28px', maxWidth: '280px', fontFamily: 'Inter' }}>
              A calm, intelligent psychological guidance platform for students and parents. Powered by science, AI, and human expertise.
            </p>

            {/* Newsletter */}
            <div style={{ marginBottom: '28px' }}>
              <div style={{ fontSize: '12px', fontFamily: 'Manrope', fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: '10px', letterSpacing: '0.04em' }}>
                Stay updated
              </div>
              <form onSubmit={handleNewsletterSubmit} style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="email"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  aria-label="Email address for MindVrik updates"
                  style={{
                    flex: 1, padding: '10px 14px', borderRadius: '10px',
                    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.12)',
                    color: 'white', fontSize: '13px', fontFamily: 'Inter', outline: 'none',
                  }}
                  data-testid="footer-email-input"
                />
                <button
                  type="submit"
                  style={{
                    padding: '10px 16px', borderRadius: '10px',
                    background: '#2A9D8F', border: 'none', cursor: 'pointer',
                    color: 'white', display: 'flex', alignItems: 'center',
                  }}
                  data-testid="footer-subscribe-btn"
                >
                  <ArrowRight size={15} />
                </button>
              </form>
              {newsletterMessage && (
                <div
                  role={newsletterStatus === 'error' ? 'alert' : 'status'}
                  style={{
                    marginTop: '8px',
                    fontSize: '12px',
                    color: newsletterStatus === 'error' ? '#F4A261' : 'rgba(255,255,255,0.62)',
                    fontFamily: 'Inter',
                  }}
                >
                  {newsletterMessage}
                </div>
              )}
            </div>

            {/* Social links */}
            <div style={{ display: 'flex', gap: '10px' }}>
              {[
                { icon: <Linkedin size={15} />, ...socialLinks.linkedin },
                { icon: <Twitter size={15} />, ...socialLinks.x },
                { icon: <Instagram size={15} />, ...socialLinks.instagram },
              ].map(({ icon, label, href, handle }) => (
                <a
                  key={label}
                  aria-label={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  title={handle ? `${label}: ${handle}` : label}
                  style={{
                    width: '36px', height: '36px', borderRadius: '10px',
                    background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)',
                    color: 'rgba(255,255,255,0.6)', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 0.2s ease', textDecoration: 'none',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(42,157,143,0.2)'; e.currentTarget.style.color = '#2A9D8F'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.6)'; }}
                  data-testid={`social-${label.toLowerCase()}`}
                >
                  {icon}
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <div style={{ fontSize: '11px', fontFamily: 'Manrope', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)', marginBottom: '20px' }}>
                {category}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {links.map(link => (
                  <a
                    key={link.label}
                    href={link.href || '#platform'}
                    onClick={e => { e.preventDefault(); handleFooterLink(link); }}
                    style={{
                      fontSize: '13px', fontFamily: 'Inter', color: 'rgba(255,255,255,0.55)',
                      textDecoration: 'none', transition: 'color 0.2s ease', display: 'block',
                    }}
                    onMouseEnter={e => e.target.style.color = 'rgba(255,255,255,0.95)'}
                    onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.55)'}
                  >
                    {link.label}
                  </a>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div style={{ height: '1px', background: 'rgba(255,255,255,0.08)', marginBottom: '28px' }} />

        {/* Bottom bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter' }}>
            &copy; 2026 MindVrik. All rights reserved. &nbsp;&middot;&nbsp;
            <a href="mailto:contact@themindvrik.com" style={{ color: 'rgba(42,157,143,0.8)', textDecoration: 'none' }}>contact@themindvrik.com</a>
          </div>
          <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', fontFamily: 'Inter', display: 'flex', alignItems: 'center', gap: '4px' }}>
            Advisory. Not diagnostic. Made with <Heart size={11} fill="rgba(244,162,97,0.8)" color="transparent" /> for growth.
          </div>
          <div style={{ display: 'flex', gap: '20px' }}>
            {['Privacy', 'Terms', 'Cookies'].map(l => (
              <a key={l} href="#contact" style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', textDecoration: 'none', fontFamily: 'Inter', transition: 'color 0.2s ease' }}
                onClick={e => { e.preventDefault(); handleNav('#contact'); }}
                onMouseEnter={e => e.target.style.color = 'rgba(255,255,255,0.7)'}
                onMouseLeave={e => e.target.style.color = 'rgba(255,255,255,0.35)'}
              >{l}</a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
